<template>
    <div>
        <!-- Form for editing details -->
        <form
            ref="auditForm"
            @submit.prevent="submitForm"
            class="d-flex flex-column form-container"
        >
            <!-- Audit Name -->
            <Field
                class="mt-0"
                label="Audit Name"
                v-model="localAudit.name"
                type="text"
                :disabled="localAudit.template_id"
                :required="true"
            />

            <!-- Audit Type - Editable at any time -->
            <div>
                <label
                    class="mb-1 col-form-label d-flex justify-content-between align-items-center"
                >
                    Type*
                    <ToolTip>
                        <template #tooltip-trigger>
                            <i class="fi fi-info" aria-label="Info"></i>
                        </template>
                        <template #tooltip-content>
                            Select Audit for a quick observation, Assessment for
                            a structured evaluation, or Report for an evaluation
                            completed externally.
                        </template>
                    </ToolTip>
                </label>
                <Field
                    v-model="localAudit.audit_type"
                    type="radio"
                    :required="true"
                    :params="{
                        options: [
                            { label: 'Audit', value: 'AUDIT' },
                            { label: 'Assessment', value: 'ASSESSMENT' },
                            { label: 'Report', value: 'REPORT' },
                        ],
                        name: 'audit-summary-type',
                    }"
                    :disabled="localAudit.template_id"
                />
            </div>

            <!-- Site -->
            <div>
                <Field
                    label="Site"
                    v-model="localAudit.site_id"
                    :required="true"
                    :disabled="siteOptions.length < 1"
                    type="select"
                    :params="{
                        options: siteOptions,
                        placeholder:
                            siteOptions.length > 0
                                ? 'Select a site'
                                : 'No sites available',
                        searchable: true,
                        allowEmpty: false,
                    }"
                />

                <span class="text-muted" style="font-size: 10pt">
                    Only users assigned to this site may view this audit
                </span>
            </div>

            <!-- Auditor -->
            <Field
                label="Auditor"
                v-model="localAudit.auditor_id"
                type="select"
                :required="true"
                :params="{
                    options: userOptions,
                    placeholder: 'Select an auditor',
                    searchable: true,
                    allowEmpty: true,
                }"
            />

            <!-- Area -->
            <div class="area-container">
                <div style="flex: 1">
                    <Field
                        label="Area"
                        v-model="localAudit.area_id"
                        :required="true"
                        :disabled="areaOptions.length < 1"
                        type="select"
                        :params="{
                            options: areaOptions,
                            placeholder: 'Select an area',
                            searchable: true,
                            allowEmpty: true,
                        }"
                        style="width: 100%"
                    />
                </div>
                <button
                    v-if="!auditAreas?.length"
                    type="button"
                    class="btn btn-link p-0 area-edit-link"
                    @click="openAreaForm"
                >
                    Edit
                </button>
            </div>

            <!-- Score -->
            <div class="field-container">
                <Field
                    v-if="
                        localAudit.score_override ||
                        !(localAudit.form_max_score > 0)
                    "
                    label="Audit Score"
                    v-model="localAudit.score_override"
                    :required="false"
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                />
            </div>

            <!-- Date -->
            <Field
                label="Date"
                v-model="localAudit.date"
                type="date"
                :required="true"
                :params="{ maxDate: today }"
            />

            <!-- Note -->
            <Field label="Note" v-model="localAudit.details" type="textarea" />

            <!-- File Upload -->
            <small
                style="
                    font-weight: 600;
                    color: var(--neutral-900);
                    line-height: 100%;
                "
                >Upload an audit file (optional)</small
            >

            <div class="image-card mt-2">
                <AdvancedUploadField
                    :multiple="true"
                    :accept="['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']"
                    v-model="localAudit.files"
                />
            </div>

            <!-- Send notification checkbox - only show in admin route for new entries -->
            <div v-if="isAdminRoute && newEntry">
                <Field
                    label="Send notification to team"
                    v-model="sendNotification"
                    type="checkbox"
                />
                <span class="text-muted fst-italic" style="font-size: 10pt">
                    Notify team members with audit access about this new audit
                </span>
            </div>
        </form>
    </div>
</template>

<script>
import moment from 'moment';
import Field from '../Field.vue';
import AdvancedUploadField from '../fields/AdvancedUploadField.vue';
import ToolTip from '../fields/ToolTip.vue';
import { useSiteStore } from '@/stores/site';
import { useAreaStore } from '@/stores/area';

export default {
    name: 'AuditSummaryForm',
    components: {
        AdvancedUploadField,
        Field,
        ToolTip,
    },
    props: {
        audit: {
            type: Object,
            default: () => ({}),
        },
        userOptions: {
            type: Array,
            required: true,
        },
        newEntry: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            files: [],
            localAudit: {},
            auditSites: [],
            siteStore: null,
            auditAreas: [],
            areaStore: null,
            sendNotification: true,
        };
    },
    computed: {
        isAdminRoute() {
            return this.$route.matched.some((r) => r.name === 'Admin');
        },
        today() {
            return new Date();
        },
        areaOptions() {
            if (this.auditAreas?.length > 0) {
                return this.auditAreas;
            }

            if (!this.areaStore || !this.areaStore.getAreaOptions) {
                return [];
            }

            return this.areaStore.getAreaOptions.map((opt) => ({
                label: opt.label ?? opt.text,
                value: opt.value,
            }));
        },
        siteOptions() {
            const storeOptions = this.siteStore?.getSiteOptions || [];
            const auditIds = new Set(
                this.auditSites?.map((s) => Number(s.value)) || []
            );
            const userIds = new Set(
                this.$userStore?.user?.sites?.map((s) => s.id) || []
            );

            return storeOptions.filter((opt) => {
                const id = Number(opt.value);

                // must be in auditSites if any are defined
                const auditOk = auditIds.size === 0 || auditIds.has(id);

                // must be in userSites if not admin and any are defined
                const userOk =
                    this.isAdminRoute || userIds.size === 0 || userIds.has(id);

                return auditOk && userOk;
            });
        },
    },
    emits: ['submit', 'open-modal', 'areas'],
    methods: {
        openAreaForm() {
            this.$emit('areas');
        },
        resetForm() {
            const today = new Date().toISOString();
            this.localAudit = {
                id: !this.newEntry ? this.audit?.id : undefined,
                template_id: this.audit?.template_id || undefined,
                form_max_score: this.audit?.form_max_score || undefined,
                name: this.audit?.name || '',
                site_id: this.audit?.site_id || '',
                date:
                    moment(this.audit?.date).format('YYYY-MM-DD') ||
                    (this.newEntry ? today : ''),
                auditor_id: this.audit?.auditor_id,
                area_id: this.audit?.area_id || '',
                details: this.audit?.details || '',
                score_override: this.audit?.score_override || null,
                files: this.audit?.files ? this.audit.files : [],
                audit_type: this.audit?.audit_type || null,
            };
        },
        submitForm() {
            const form = this.$refs.auditForm;

            // 1) run native validation check
            if (!form.checkValidity()) {
                form.reportValidity();

                return; // stop here—don't save
            }
            // Prepare form data by transforming files into an array of file IDs
            const preparedData = this.prepareFormDataForSubmission();
            this.$emit(
                'submit',
                preparedData,
                this.newEntry,
                this.sendNotification
            );
        },
        prepareFormDataForSubmission() {
            // Only record completed_at if not already completed
            if (this.newEntry) {
                this.localAudit.completed_at = Date.now();
            }
            // Deep clone formData to avoid mutating the original object
            const formDataToSubmit = JSON.parse(
                JSON.stringify(this.localAudit)
            );

            // Transform files: extract file IDs if any exist
            if (formDataToSubmit.files && formDataToSubmit.files.length > 0) {
                formDataToSubmit.files = formDataToSubmit.files.map(
                    (file) => file.id
                );
            }

            // Set app_admin_only to true when saving from admin panel
            if (
                this.$route.name === 'Admin' ||
                this.$route.name === 'AdminPanel' ||
                this.$route.name === 'Company Detail' ||
                this.$route.matched.some((r) => r.name === 'Admin')
            ) {
                formDataToSubmit.app_admin_only = true;
            }

            return formDataToSubmit;
        },
        getEndpointForCategory(category, isNewEntry) {
            // Use single audit endpoint for all categories
            const action = isNewEntry ? 'create' : 'update';
            return `audit/${action}`;
        },
        loadAuditTemplateOptions() {
            const params = {
                where: { id: Number(this.localAudit.template_id) },
                fields: ['id'],
                include: {
                    sites: {
                        fields: ['id', 'name'],
                    },
                    areas: {
                        fields: ['id', 'name'],
                    },
                },
            };
            this.$getData('audit/template/read', params).then((response) => {
                if (response?.success && response.data) {
                    const { id, sites, areas } = response.data?.[0];

                    this.auditSites = this.populateOptions(
                        sites,
                        this.localAudit.site_id,
                        this.siteStore?.getSiteOptions
                    );

                    this.auditAreas = this.populateOptions(
                        areas,
                        this.localAudit.area_id,
                        this.areaStore?.getAreaOptions
                    );
                } else {
                    this.$alertStore.pushError(
                        response?.message || 'Failed to load template'
                    );
                }
            });
        },
        populateOptions(rawOptions, currentId, storeOptions) {
            let options = rawOptions
                ? rawOptions.map((o) => ({
                      label: o.name,
                      value: String(o.id),
                  }))
                : [];

            if (options.length > 0 && currentId) {
                if (!options.some((o) => o.value === String(currentId))) {
                    const fromStore = storeOptions?.find(
                        (o) => String(o.value) === String(currentId)
                    );
                    if (fromStore) options.push(fromStore);
                }
            }

            return options;
        },
    },
    mounted() {
        this.resetForm();
        this.siteStore = useSiteStore();
        this.siteStore.syncSites();
        this.areaStore = useAreaStore();
        this.areaStore.syncAreas();

        if (this.audit?.template_id) {
            this.loadAuditTemplateOptions();
        }
    },
};
</script>

<style scoped>
.image-card {
    padding: var(--space-3);
    gap: 0.75rem;
    background-color: var(--neutral-400);
    border-radius: var(--space-3);
}

.field-container {
    position: relative;
    width: 100%;
}

.edit-area {
    cursor: pointer;
    color: var(--primary-400, #678277);
    font-family: Inter;
    font-size: 0.9375rem;
    font-style: normal;
    font-weight: 600;
    line-height: 1.125rem;
    /* 120% */
    text-decoration-line: underline;
    white-space: nowrap;
    margin: 1rem 0;
}

.score-display {
    position: absolute;
    top: 0;
    right: 0;
    font-size: 0.875rem;
    line-height: 0.875rem;
    padding: 0 0.75rem;
}

.form-container {
    gap: var(--space-4);
}

.area-edit-link {
    cursor: pointer;
    color: var(--primary-400, #678277);
    font-family: Inter;
    font-size: 0.9375rem;
    font-style: normal;
    font-weight: 600;
    line-height: 1.125rem;
    text-decoration-line: underline;
    white-space: nowrap;
    margin-bottom: 0.625rem;
}

.area-edit-link:hover {
    color: var(--primary-400, #678277);
    background-color: transparent;
}

.area-container {
    display: flex;
    align-items: flex-end;
    gap: 0.75rem;
}
</style>
